import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import MobileSplashGate from "../components/MobileSplashGate";
import SecureLogger from "../utils/secureLogger";
import { setupApiInterceptor } from "../utils/apiInterceptor";
import API_CONFIG from "../config/api.js";
import {
  decideUserStatusFailure,
  getUserStatusRetryDelayMs,
  USER_STATUS_FAILURE_ACTIONS,
  USER_STATUS_MAX_ATTEMPTS,
} from "./authStatusPolicy";
import {
  mergeRefreshedUserStatus,
  mergeSavedUserProfile,
} from "./userStatusState";
import { clearSubscriptionBillingBannerDismissals } from "../utils/subscriptionBillingBannerDismissal";
import { clearFeedbackWidgetDismissals } from "../gestion-feedback/feedbackWidgetSession";
import { SUBSCRIPTION_ACCESS_UPDATED_EVENT } from "../utils/subscriptionAccessEvents";
import { useOptionalSelection } from "../SelectionContext";
import { isSubscriptionPortalOnlyForCompany } from "../utils/subscriptionCompanyScope";

// 1. Créer le contexte
const AuthContext = createContext(null);

// URL de base de l'API - idéalement à récupérer d'une configuration centralisée
const API_BASE_URL = API_CONFIG.BASE_URL; // Configuration centralisée
const SESSION_REFRESH_WINDOW_MS = 15 * 60 * 1000;
const SESSION_ACTIVITY_WINDOW_MS = 15 * 60 * 1000;
const SESSION_REFRESH_CHECK_INTERVAL_MS = 60 * 1000;
const SESSION_ACTIVITY_THROTTLE_MS = 30 * 1000;
const SESSION_REFRESH_RETRY_GUARD_MS = 30 * 1000;

const wait = (delayMs) =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

const readResponsePayload = async (response) => {
  try {
    return await response.json();
  } catch (_error) {
    return null;
  }
};

export const runCoalescedPermissionRefresh = (
  permissionRefreshInFlightRef,
  refreshFactory
) => {
  if (permissionRefreshInFlightRef.current) {
    return permissionRefreshInFlightRef.current;
  }

  const refreshPromise = Promise.resolve().then(refreshFactory);
  const trackedPromise = refreshPromise.finally(() => {
    if (permissionRefreshInFlightRef.current === trackedPromise) {
      permissionRefreshInFlightRef.current = null;
    }
  });

  permissionRefreshInFlightRef.current = trackedPromise;
  return trackedPromise;
};

const getJwtExpirationTimestamp = (jwtToken) => {
  if (!jwtToken) {
    return null;
  }

  try {
    const [, payload] = jwtToken.split(".");
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload =
      normalizedPayload +
      "=".repeat((4 - (normalizedPayload.length % 4 || 4)) % 4);
    const decodedPayload = window.atob(paddedPayload);
    const parsedPayload = JSON.parse(decodedPayload);

    return Number.isFinite(parsedPayload?.exp)
      ? parsedPayload.exp * 1000
      : null;
  } catch (error) {
    SecureLogger.warn("Unable to decode JWT expiration timestamp", {
      message: error?.message,
    });
    return null;
  }
};

// Controle de coherence du cache uniquement : l'API reste l'autorite d'authentification.
const getJwtUserId = (jwtToken) => {
  try {
    const payload = jwtToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const value = JSON.parse(window.atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, "=")));
    const id = Number(value.id);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  } catch { return null; }
};

// 2. Créer le Provider
export const AuthProvider = ({ children }) => {
  const selection = useOptionalSelection();
  const selectedCompany = selection?.selectedCompany;
  const selectedCompanyRef = useRef(selectedCompany);
  selectedCompanyRef.current = selectedCompany;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null); // Stockera l'objet utilisateur complet (incl. assigned_warehouses)
  const [token, setToken] = useState(localStorage.getItem("authToken")); // Initialiser depuis localStorage
  const [isLoading, setIsLoading] = useState(true); // Pour gérer le chargement initial
  // Use a ref to store last refresh timestamp to avoid effect dependency loops
  const lastRefreshTimestampRef = useRef(0);
  const permissionRefreshInFlightRef = useRef(null);
  const authSessionGenerationRef = useRef(0);
  const lastUserActivityRef = useRef(Date.now());
  const refreshInFlightRef = useRef(null);
  const userStatusRefreshInFlightRef = useRef(null);
  const lastSessionRefreshAttemptRef = useRef(0);
  const pushServicesStartedForUserRef = useRef(null);
  const renderedAuthSessionGeneration = authSessionGenerationRef.current;
  const sessionSnapshotRef = useRef({ token, userId: user?.id });
  sessionSnapshotRef.current = { token, userId: user?.id };

  // Ref pour fournir l'accès aux méthodes auth aux intercepteurs
  const authRef = useRef();

  // Fonction de déconnexion (déplacée ici pour être accessible par les hooks ci-dessous)
  const logout = useCallback((options = {}) => {
    const { reason = "USER_INITIATED", notifyServer = true } = options;
    const currentToken = localStorage.getItem("authToken");

    // Notifier le backend pour tracer le LOGOUT (audit) avant de purger le token.
    // Fire-and-forget : on n'attend pas la reponse pour ne pas bloquer la sortie.
    if (notifyServer && currentToken) {
      try {
        fetch(`${API_BASE_URL}/api/auth/session/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason }),
          keepalive: true,
          skipGlobalLoading: true,
        }).catch(() => {});
      } catch (_err) {
        // best-effort, ne doit jamais empecher le logout cote client
      }
    }

    // Nettoyer les timers/listeners du moniteur push pour éviter les fuites
    import("../utils/pushSubscriptionMonitor")
      .then((module) => {
        module.stopPushSubscriptionMonitor();
      })
      .catch((error) => {
        SecureLogger.warn("Push subscription monitor cleanup failed", {
          message: error?.message,
        });
      });

    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    clearFeedbackWidgetDismissals();
    authSessionGenerationRef.current += 1;
    lastRefreshTimestampRef.current = 0;
    permissionRefreshInFlightRef.current = null;
    refreshInFlightRef.current = null;
    userStatusRefreshInFlightRef.current = null;
    lastSessionRefreshAttemptRef.current = 0;
    lastUserActivityRef.current = 0;
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    SecureLogger.info("User logged out successfully");
    // La redirection sera gérée par le composant qui appelle logout ou par ProtectedRoute
  }, []); // Ajout des dépendances vides car elle ne dépend de rien dans ce scope

  // Log user state on every render to track changes
  useEffect(() => {
    SecureLogger.debug("User state changed", {
      hasUser: !!user,
      hasWarehouses: !!user?.assigned_warehouses,
    });
  }, [user]);

  // Fonction pour rafraîchir les permissions de l'utilisateur
  const refreshUserPermissions = useCallback(
    async (force = false) => {
      SecureLogger.debug("Refresh user permissions called", { force });
      if (!token || !user || !user.id) {
        SecureLogger.debug("Permission refresh aborted: missing required data");
        return;
      }

      if (user.subscription_portal_only) {
        SecureLogger.debug(
          "Permission refresh skipped for subscription-portal-only session"
        );
        return;
      }

      const permissionRefreshGeneration = renderedAuthSessionGeneration;
      const permissionRefreshToken = token;
      const primaryCompanyId = user.company_id || user.company?.id;
      const requestedCompanyId = Number(selectedCompany || primaryCompanyId);
      const isPermissionRefreshSessionCurrent = () =>
        authSessionGenerationRef.current === permissionRefreshGeneration &&
        localStorage.getItem("authToken") === permissionRefreshToken &&
        Number(selectedCompanyRef.current || primaryCompanyId) ===
          requestedCompanyId;

      if (!isPermissionRefreshSessionCurrent()) {
        SecureLogger.debug(
          "Permission refresh skipped - stale session callback"
        );
        return;
      }

      if (permissionRefreshInFlightRef.current) {
        SecureLogger.debug(
          "Permission refresh coalesced - request already running"
        );
        return permissionRefreshInFlightRef.current;
      }

      const now = Date.now();
      if (!force && now - lastRefreshTimestampRef.current < 60000) {
        SecureLogger.debug(
          "Permission refresh skipped - too soon since last refresh"
        );
        return;
      }

      SecureLogger.debug("Fetching user permissions");

      return runCoalescedPermissionRefresh(
        permissionRefreshInFlightRef,
        async () => {
          try {
            const response = await fetch(
              `${API_BASE_URL}/api/users/${user.id}/permissions?company_id=${requestedCompanyId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
                skipGlobalLoading: true,
              }
            );

            if (!isPermissionRefreshSessionCurrent()) {
              SecureLogger.debug("Stale permission refresh response ignored");
              return;
            }

            if (!response.ok) {
              const errorPayload = await readResponsePayload(response);

              if (!isPermissionRefreshSessionCurrent()) {
                SecureLogger.debug(
                  "Stale permission refresh error response ignored"
                );
                return;
              }

              const decision = decideUserStatusFailure({
                status: response.status,
                code: errorPayload?.code,
              });

              if (decision.action === USER_STATUS_FAILURE_ACTIONS.LOGOUT) {
                SecureLogger.warn(
                  "Permission refresh invalidated the session",
                  {
                    status: response.status,
                    code: errorPayload?.code,
                  }
                );
                logout({ reason: decision.reason, notifyServer: false });
                return;
              }

              SecureLogger.warn(
                "Permission refresh failed without invalidating the session",
                {
                  status: response.status,
                  code: errorPayload?.code,
                  retryable:
                    decision.action === USER_STATUS_FAILURE_ACTIONS.RETRY,
                }
              );
              return;
            }

            const data = await response.json();

            if (!isPermissionRefreshSessionCurrent()) {
              SecureLogger.debug("Stale permission refresh payload ignored");
              return;
            }

            const updatedPermissions = data.permissions || data;

            SecureLogger.debug("User permissions fetched successfully", {
              permissionCount: updatedPermissions?.length || 0,
            });

            const updatedPermissionsArray = Array.isArray(updatedPermissions)
              ? [...updatedPermissions].sort()
              : [];
            setUser((currentUser) => {
              if (
                !currentUser ||
                Number(currentUser.id) !== Number(user.id) ||
                !isPermissionRefreshSessionCurrent()
              ) return currentUser;
              const currentPermissionsArray = Array.isArray(currentUser.permissions)
                ? [...currentUser.permissions].sort()
                : [];
              if (JSON.stringify(currentPermissionsArray) === JSON.stringify(updatedPermissionsArray)) {
                return currentUser;
              }
              const updatedUser = { ...currentUser, permissions: updatedPermissionsArray };
              localStorage.setItem("userData", JSON.stringify(updatedUser));
              return updatedUser;
            });

            lastRefreshTimestampRef.current = now;
          } catch (error) {
            SecureLogger.warn(
              "Permission refresh unavailable - preserving current session",
              {
                errorType: error.name,
                message: error.message,
              }
            );
          }
        }
      );
    },
    [token, user, logout, renderedAuthSessionGeneration, selectedCompany]
  );

  useEffect(() => {
    lastRefreshTimestampRef.current = 0;
    permissionRefreshInFlightRef.current = null;
    setUser((currentUser) => {
      if (!currentUser || localStorage.getItem("authToken") !== token || authSessionGenerationRef.current !== renderedAuthSessionGeneration) return currentUser;
      const updatedUser = { ...currentUser, permissions: [] };
      localStorage.setItem("userData", JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, [selectedCompany]);

  // Rafraîchir le statut et les assignations sans confondre une panne avec une session invalide.
  const refreshUserStatus = useCallback(async () => {
    const currentUser = user;
    const currentToken = token;
    const primaryCompanyId = currentUser?.company_id || currentUser?.company?.id;
    const normalizeCompanyContextId = (value) => {
      const id = Number(value);
      return Number.isSafeInteger(id) && id > 0 ? id : null;
    };
    const statusCompanyId = normalizeCompanyContextId(
      selectedCompany || primaryCompanyId
    );
    const isStatusContextCurrent = () =>
      normalizeCompanyContextId(selectedCompanyRef.current || primaryCompanyId) ===
      statusCompanyId;
    const requestKey = `${currentToken}:${statusCompanyId || "global"}`;

    SecureLogger.debug("User status refresh initiated");
    if (!currentToken || !currentUser || !currentUser.id) {
      SecureLogger.debug(
        "User status refresh aborted - missing required data",
        { hasToken: !!currentToken, hasUser: !!currentUser }
      );
      return false;
    }

    if (isSubscriptionPortalOnlyForCompany(currentUser, selectedCompany)) {
      SecureLogger.debug(
        "User status refresh skipped for subscription-portal-only session"
      );
      return false;
    }

    if (userStatusRefreshInFlightRef.current === requestKey) {
      SecureLogger.debug("User status refresh skipped - request already running");
      return false;
    }

    userStatusRefreshInFlightRef.current = requestKey;
    const userIdToFetch = currentUser.id;
    const statusUrl = `${API_BASE_URL}/api/users/${userIdToFetch}?include_accessible_warehouses=true${statusCompanyId ? `&company_id=${statusCompanyId}` : ""}`;
    SecureLogger.debug("Fetching user status and assignments");

    try {
      let response = null;

      for (let attempt = 1; attempt <= USER_STATUS_MAX_ATTEMPTS; attempt++) {
        if (localStorage.getItem("authToken") !== currentToken || !isStatusContextCurrent()) {
          SecureLogger.debug("User status refresh stopped - session changed");
          return false;
        }

        try {
          response = await fetch(statusUrl, {
            headers: {
              Authorization: `Bearer ${currentToken}`,
              "Content-Type": "application/json",
            },
            skipGlobalLoading: true,
          });
        } catch (error) {
          if (error.name === "AbortError") {
            return false;
          }

          if (localStorage.getItem("authToken") !== currentToken || !isStatusContextCurrent()) {
            SecureLogger.debug("User status network error ignored - session changed");
            return false;
          }

          if (attempt < USER_STATUS_MAX_ATTEMPTS) {
            const retryDelayMs = getUserStatusRetryDelayMs(attempt);
            SecureLogger.warn("User status network error - retry scheduled", {
              attempt,
              retryDelayMs,
              errorType: error.name || "NETWORK_ERROR",
            });
            await wait(retryDelayMs);
            continue;
          }

          SecureLogger.warn(
            "User status service unreachable - preserving current session",
            {
              attempts: attempt,
              errorType: error.name || "NETWORK_ERROR",
            }
          );
          return false;
        }

        if (localStorage.getItem("authToken") !== currentToken || !isStatusContextCurrent()) {
          SecureLogger.debug("Stale user status response ignored");
          return false;
        }

        if (response.ok) {
          break;
        }

        const errorPayload = await readResponsePayload(response);
        const decision = decideUserStatusFailure({
          status: response.status,
          code: errorPayload?.code,
        });

        if (decision.action === USER_STATUS_FAILURE_ACTIONS.LOGOUT) {
          SecureLogger.warn("User status check invalidated the session", {
            status: response.status,
            code: errorPayload?.code,
          });
          logout({ reason: decision.reason, notifyServer: false });
          return false;
        }

        if (
          decision.action === USER_STATUS_FAILURE_ACTIONS.RETRY &&
          attempt < USER_STATUS_MAX_ATTEMPTS
        ) {
          const retryDelayMs = getUserStatusRetryDelayMs(
            attempt,
            response.headers?.get?.("Retry-After")
          );
          SecureLogger.warn(
            "User status service temporarily unavailable - retry scheduled",
            {
              status: response.status,
              code: errorPayload?.code,
              attempt,
              retryDelayMs,
            }
          );
          await wait(retryDelayMs);
          continue;
        }

        SecureLogger.warn(
          "User status check failed without invalidating the session",
          {
            status: response.status,
            code: errorPayload?.code,
            attempts: attempt,
            retryable:
              decision.action === USER_STATUS_FAILURE_ACTIONS.RETRY,
          }
        );
        return false;
      }

      if (!response?.ok) {
        return false;
      }

      const fetchedUserData = await response.json();
      if (!isStatusContextCurrent() || localStorage.getItem("authToken") !== currentToken) {
        SecureLogger.debug("Stale user status payload ignored");
        return false;
      }
      SecureLogger.debug("User data fetched successfully");

      // **Crucial Check:** Verify the fetched ID matches the requested ID
      if (fetchedUserData.id !== userIdToFetch) {
        SecureLogger.error("User ID mismatch detected, aborting update");
        return false;
      }

      // Vérifier si le statut est 'disabled'
      if (fetchedUserData && fetchedUserData.status === "disabled") {
        SecureLogger.warn("User account is disabled, logging out");
        logout({ reason: "ACCOUNT_INACTIVE" });
        return false;
      }

      if (!isStatusContextCurrent()) return false;
      setUser((latestUser) => {
        if (
          !latestUser ||
          Number(latestUser.id) !== Number(userIdToFetch) ||
          localStorage.getItem("authToken") !== currentToken ||
          !isStatusContextCurrent()
        ) return latestUser;
        const refreshedWarehouses =
          fetchedUserData.access_scope === "global"
            ? Array.isArray(fetchedUserData.accessible_warehouses)
              ? fetchedUserData.accessible_warehouses
              : Array.isArray(latestUser.assigned_warehouses)
                ? latestUser.assigned_warehouses
                : []
            : Array.isArray(fetchedUserData.assigned_warehouses)
              ? fetchedUserData.assigned_warehouses
              : [];
        const updatedUserObject = mergeRefreshedUserStatus({
          currentUser: latestUser,
          fetchedUserData,
          refreshedWarehouses,
        });
        updatedUserObject.subscription_portal_only =
          isSubscriptionPortalOnlyForCompany(updatedUserObject, selectedCompany);
        if (JSON.stringify(updatedUserObject) === JSON.stringify(latestUser)) {
          return latestUser;
        }
        localStorage.setItem("userData", JSON.stringify(updatedUserObject));
        localStorage.setItem("userDataCacheVersion", "2024-09-26-trial-fix");
        return updatedUserObject;
      });
      return true;
    } catch (error) {
      if (error.name !== "AbortError") {
        SecureLogger.warn(
          "Unexpected user status refresh error - preserving current session",
          {
            errorType: error.name,
            message: error.message,
          }
        );
      }
      return false;
    } finally {
      if (userStatusRefreshInFlightRef.current === requestKey) {
        userStatusRefreshInFlightRef.current = null;
      }
    }
  }, [token, user, logout, selectedCompany]);

  const syncUserProfile = useCallback((profileData) => {
    setUser((currentUser) => {
      if (localStorage.getItem("authToken") !== token || authSessionGenerationRef.current !== renderedAuthSessionGeneration) return currentUser;
      const updatedUser = mergeSavedUserProfile(currentUser, profileData);

      if (updatedUser && updatedUser !== currentUser) {
        localStorage.setItem("userData", JSON.stringify(updatedUser));
      }

      return updatedUser;
    });
  }, [token, renderedAuthSessionGeneration]);

  const syncSubscriptionAccess = useCallback((payload = {}) => {
    const incomingAccess =
      payload.subscription_access || payload.access || payload;
    if (!incomingAccess?.access_state) return;

    setUser((currentUser) => {
      if (!currentUser || Number(currentUser.is_superadmin) === 1 || localStorage.getItem("authToken") !== token || authSessionGenerationRef.current !== renderedAuthSessionGeneration) {
        return currentUser;
      }

      const companyId = Number(
        payload.company_id || incomingAccess.company_id || currentUser.company_id,
      );
      const subscriptionAccess = {
        ...incomingAccess,
        company_id:
          Number.isSafeInteger(companyId) && companyId > 0
            ? companyId
            : null,
      };
      const updatedUser = {
        ...currentUser,
        subscription_access: subscriptionAccess,
      };
      updatedUser.subscription_portal_only = isSubscriptionPortalOnlyForCompany(
        updatedUser,
        selectedCompany,
      );
      localStorage.setItem("userData", JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, [selectedCompany, token, renderedAuthSessionGeneration]);

  useEffect(() => {
    setUser((currentUser) => {
      if (!currentUser?.subscription_access?.company_id || localStorage.getItem("authToken") !== token || authSessionGenerationRef.current !== renderedAuthSessionGeneration) return currentUser;
      const portalOnly = isSubscriptionPortalOnlyForCompany(currentUser, selectedCompany);
      if (currentUser.subscription_portal_only === portalOnly) return currentUser;
      const updatedUser = { ...currentUser, subscription_portal_only: portalOnly };
      localStorage.setItem("userData", JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, [selectedCompany]);

  useEffect(() => {
    const handleSubscriptionAccessUpdate = (event) => {
      syncSubscriptionAccess(event.detail || {});
    };
    window.addEventListener(
      SUBSCRIPTION_ACCESS_UPDATED_EVENT,
      handleSubscriptionAccessUpdate,
    );
    return () => {
      window.removeEventListener(
        SUBSCRIPTION_ACCESS_UPDATED_EVENT,
        handleSubscriptionAccessUpdate,
      );
    };
  }, [syncSubscriptionAccess]);

  const refreshSessionToken = useCallback(
    async (reason = "unknown") => {
      const sessionRefreshGeneration = renderedAuthSessionGeneration;
      const sessionRefreshToken = token;
      const isSessionRefreshCurrent = () =>
        authSessionGenerationRef.current === sessionRefreshGeneration &&
        localStorage.getItem("authToken") === sessionRefreshToken;

      if (
        !token ||
        !user?.id ||
        user.subscription_portal_only ||
        !isSessionRefreshCurrent()
      ) {
        return false;
      }

      if (refreshInFlightRef.current) {
        return false;
      }

      const now = Date.now();
      if (
        now - lastSessionRefreshAttemptRef.current <
        SESSION_REFRESH_RETRY_GUARD_MS
      ) {
        return false;
      }

      const refreshAttempt = Symbol("session-refresh-attempt");
      refreshInFlightRef.current = refreshAttempt;
      lastSessionRefreshAttemptRef.current = now;

      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/session/refresh`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          skipGlobalLoading: true,
        });

        const responseData = await response.json().catch(() => null);

        if (!isSessionRefreshCurrent()) {
          SecureLogger.debug("Stale session refresh response ignored", {
            reason,
          });
          return false;
        }

        if (!response.ok) {
          SecureLogger.warn("Session refresh rejected by backend", {
            reason,
            status: response.status,
            code: responseData?.code,
          });

          const decision = decideUserStatusFailure({
            status: response.status,
            code: responseData?.code,
          });

          if (decision.action === USER_STATUS_FAILURE_ACTIONS.LOGOUT) {
            logout({ reason: decision.reason, notifyServer: false });
          }

          return false;
        }

        if (!responseData?.token) {
          throw new Error("Missing token in session refresh response");
        }

        const tokenChanged = responseData.token !== sessionRefreshToken;
        if (tokenChanged) {
          authSessionGenerationRef.current += 1;
          lastRefreshTimestampRef.current = 0;
          permissionRefreshInFlightRef.current = null;
        }
        localStorage.setItem("authToken", responseData.token);
        setToken(responseData.token);
        lastUserActivityRef.current = Date.now();
        SecureLogger.info("Session refreshed silently", {
          reason,
          expiresAt: responseData?.expiresAt,
        });
        return true;
      } catch (error) {
        SecureLogger.warn("Session refresh failed", {
          reason,
          message: error?.message,
        });
        return false;
      } finally {
        if (refreshInFlightRef.current === refreshAttempt) {
          refreshInFlightRef.current = null;
        }
      }
    },
    [
      token,
      user?.id,
      user?.subscription_portal_only,
      logout,
      renderedAuthSessionGeneration,
    ]
  );

  const checkAndRefreshSession = useCallback(
    async (reason = "interval") => {
      if (
        !isAuthenticated ||
        !token ||
        !user?.id ||
        user.subscription_portal_only
      ) {
        return false;
      }

      const expirationTimestamp = getJwtExpirationTimestamp(token);
      if (!expirationTimestamp) {
        return false;
      }

      const remainingMs = expirationTimestamp - Date.now();
      if (remainingMs <= 0 || remainingMs > SESSION_REFRESH_WINDOW_MS) {
        return false;
      }

      const inactivityMs = Date.now() - lastUserActivityRef.current;
      if (inactivityMs > SESSION_ACTIVITY_WINDOW_MS) {
        SecureLogger.debug("Session refresh skipped - no recent user activity", {
          reason,
          inactivityMs,
        });
        return false;
      }

      return refreshSessionToken(reason);
    },
    [
      isAuthenticated,
      token,
      user?.id,
      user?.subscription_portal_only,
      refreshSessionToken,
    ]
  );

  const restoreStoredSession = useCallback(() => {
    const storedToken = localStorage.getItem("authToken");
    const storedUserData = localStorage.getItem("userData");
    const CACHE_VERSION = "2024-09-26-trial-fix";
    authSessionGenerationRef.current += 1;
    lastRefreshTimestampRef.current = 0;
    permissionRefreshInFlightRef.current = null;
    refreshInFlightRef.current = null;
    userStatusRefreshInFlightRef.current = null;
    lastSessionRefreshAttemptRef.current = 0;
    if (storedToken && storedUserData) {
      try {
        const parsedUser = JSON.parse(storedUserData);
        const tokenUserId = getJwtUserId(storedToken);
        if (!parsedUser?.id || (tokenUserId !== null && Number(parsedUser.id) !== tokenUserId)) {
          // Un login dans un autre onglet peut encore ecrire son profil.
          // Ne jamais afficher l'ancien compte ni effacer la nouvelle connexion.
          SecureLogger.warn("Cached profile does not match the current session");
          setToken(null); setUser(null); setIsAuthenticated(false); setIsLoading(false);
          return;
        }
        parsedUser.assigned_warehouses = Array.isArray(
          parsedUser.assigned_warehouses
        )
          ? parsedUser.assigned_warehouses
          : [];
        lastUserActivityRef.current = Date.now();
        setToken(storedToken);
        setUser(parsedUser);
        setIsAuthenticated(true);
        SecureLogger.info("User session restored from localStorage");
        localStorage.setItem("userDataCacheVersion", CACHE_VERSION);
        // L'effet de session ci-dessous relit ensuite statut et permissions
        // avec le profil et le token effectivement restaures.
      } catch (error) {
        SecureLogger.error("Failed to parse user data from localStorage", {
          message: error.message,
        });
        setToken(null); setUser(null); setIsAuthenticated(false);
      }
    } else {
      setToken(null); setUser(null); setIsAuthenticated(false);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    restoreStoredSession();
    const syncOtherTab = (event) => {
      if (event?.type === "storage" && event.key !== null && !["authToken", "userData"].includes(event.key)) return;
      let storedUserId;
      try { storedUserId = JSON.parse(localStorage.getItem("userData"))?.id; } catch { storedUserId = null; }
      if (localStorage.getItem("authToken") === sessionSnapshotRef.current.token &&
        Number(storedUserId || 0) === Number(sessionSnapshotRef.current.userId || 0)) return;
      restoreStoredSession();
    };
    window.addEventListener("storage", syncOtherTab);
    window.addEventListener("focus", syncOtherTab);
    return () => {
      window.removeEventListener("storage", syncOtherTab);
      window.removeEventListener("focus", syncOtherTab);
    };
  }, [restoreStoredSession]);

  // Le timer suit la session et l'entreprise, sans redémarrer à chaque setUser.
  // Ses callbacks restent à jour pour les prochains rafraîchissements.
  const periodicRefreshRef = useRef(null);
  periodicRefreshRef.current = { refreshUserPermissions, refreshUserStatus };
  const refreshCompanyId = selectedCompany || user?.company_id || user?.company?.id;
  const subscriptionPortalOnly = Boolean(user?.subscription_portal_only);

  // Configurer un intervalle pour rafraîchir périodiquement les permissions ET le statut
  useEffect(() => {
    if (
      isAuthenticated &&
      token &&
      user?.id &&
      !subscriptionPortalOnly
    ) {
      // Rafraîchir immédiatement au démarrage
      periodicRefreshRef.current.refreshUserPermissions(true);
      periodicRefreshRef.current.refreshUserStatus();

      // Puis mettre en place un intervalle de rafraîchissement (toutes les 30 minutes)
      // OPTIMISÉ CPU: Réduit de 5min à 30min - les permissions changent rarement
      const interval = setInterval(() => {
        SecureLogger.debug("Periodic refresh of permissions and status");
        periodicRefreshRef.current.refreshUserPermissions();
        periodicRefreshRef.current.refreshUserStatus();
      }, 1800000); // 1800000 ms = 30 minutes (était 5 min)

      // Nettoyer l'intervalle lors du démontage ou de la déconnexion
      return () => clearInterval(interval);
    }
  }, [
    isAuthenticated,
    token,
    user?.id,
    subscriptionPortalOnly,
    refreshCompanyId,
    renderedAuthSessionGeneration,
  ]);

  useEffect(() => {
    if (
      !isAuthenticated ||
      !token ||
      !user?.id ||
      user.subscription_portal_only
    ) {
      pushServicesStartedForUserRef.current = null;
      return;
    }

    const pushSessionKey = `${user.id}:${token}`;
    if (pushServicesStartedForUserRef.current === pushSessionKey) {
      return;
    }

    pushServicesStartedForUserRef.current = pushSessionKey;

    import("../utils/webPushHelper")
      .then((module) => {
        return module.initializePushNotifications();
      })
      .catch((error) => {
        console.error(
          "[Auth] Erreur initialisation push notifications:",
          error
        );
      });

    import("../utils/pushSubscriptionMonitor")
      .then((module) => {
        module.startPushSubscriptionMonitor();
      })
      .catch((error) => {
        console.error(
          "[Auth] Erreur demarrage moniteur push:",
          error
        );
      });

  }, [isAuthenticated, token, user?.id, user?.subscription_portal_only]);

  useEffect(() => {
    if (!isAuthenticated || !token || !user?.id) {
      return undefined;
    }

    const passiveEventOptions = { passive: true };

    const markUserActivity = () => {
      const now = Date.now();
      if (now - lastUserActivityRef.current >= SESSION_ACTIVITY_THROTTLE_MS) {
        lastUserActivityRef.current = now;
      }

      checkAndRefreshSession("user_activity");
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        lastUserActivityRef.current = Date.now();
        checkAndRefreshSession("visibility_change");
      }
    };

    const handleWindowFocus = () => {
      lastUserActivityRef.current = Date.now();
      checkAndRefreshSession("window_focus");
    };

    window.addEventListener("pointerdown", markUserActivity, passiveEventOptions);
    window.addEventListener("keydown", markUserActivity);
    window.addEventListener("scroll", markUserActivity, passiveEventOptions);
    window.addEventListener("touchstart", markUserActivity, passiveEventOptions);
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const interval = setInterval(() => {
      checkAndRefreshSession("interval");
    }, SESSION_REFRESH_CHECK_INTERVAL_MS);

    checkAndRefreshSession("startup");

    return () => {
      clearInterval(interval);
      window.removeEventListener(
        "pointerdown",
        markUserActivity,
        passiveEventOptions
      );
      window.removeEventListener("keydown", markUserActivity);
      window.removeEventListener("scroll", markUserActivity, passiveEventOptions);
      window.removeEventListener(
        "touchstart",
        markUserActivity,
        passiveEventOptions
      );
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, token, user?.id, checkAndRefreshSession]);

  // Fonction de connexion
  const login = (newToken, userData) => {
    // Ensure assigned_warehouses is an array, default to empty if missing/null
    const userDataWithDefaults = {
      ...userData,
      assigned_warehouses: Array.isArray(userData.assigned_warehouses)
        ? userData.assigned_warehouses
        : [],
    };
    const preferredCompanyId = Number(userDataWithDefaults.preferred_company_id);
    if (
      Number.isSafeInteger(preferredCompanyId) && preferredCompanyId > 0 &&
      userDataWithDefaults.assigned_warehouses.some(
        (warehouse) => Number(warehouse.company_id) === preferredCompanyId,
      )
    ) {
      selection?.setSelectedCompany(preferredCompanyId);
      selection?.setSelectedWarehouse(null);
    }
    authSessionGenerationRef.current += 1;
    lastRefreshTimestampRef.current = 0;
    permissionRefreshInFlightRef.current = null;
    refreshInFlightRef.current = null;
    lastSessionRefreshAttemptRef.current = 0;
    lastUserActivityRef.current = Date.now();
    // Une fermeture ne vaut que pour la connexion précédente. Les rappels
    // encore actifs doivent donc réapparaître après toute authentification
    // explicite, sans être réactivés lors d'un simple rafraîchissement JWT.
    clearSubscriptionBillingBannerDismissals();
    clearFeedbackWidgetDismissals();
    localStorage.setItem("authToken", newToken);
    localStorage.setItem("userData", JSON.stringify(userDataWithDefaults));
    setToken(newToken);
    setUser(userDataWithDefaults);
    setIsAuthenticated(true);
    SecureLogger.info("User logged in successfully");

  };

  // La fonction logout est maintenant définie plus haut avec useCallback

  // Fonction pour vérifier si l'utilisateur a une permission spécifique
  const hasPermission = useCallback((permissionKey) => {
    // SysAdmin a toujours accès à tout (flag ou role)
    if (user && (user.is_superadmin || user.role === "SysAdmin")) {
      SecureLogger.debug("Permission granted", {
        permission: permissionKey,
        reason: user.is_superadmin ? "SysAdmin flag" : "SysAdmin role",
      });
      return true;
    }

    if (!user || !user.permissions) return false;
    if (!Array.isArray(user.permissions)) {
      SecureLogger.warn("Invalid permissions format detected", {
        type: typeof user.permissions,
      });
      return false;
    }
    const hasExact = user.permissions.includes(permissionKey);
    SecureLogger.debug("Permission check completed", {
      permission: permissionKey,
      granted: hasExact,
    });
    return hasExact;
  }, [user]);

  // Valeur fournie par le contexte
  const value = {
    isAuthenticated,
    user, // user object now contains assigned_warehouses
    token,
    isLoading,
    login,
    logout,
    hasPermission,
    refreshUserPermissions,
    refreshUserStatus, // <- Exposer la nouvelle fonction (optionnel)
    syncUserProfile,
    syncSubscriptionAccess,
  };

  // Mettre à jour la référence avec les dernières méthodes
  authRef.current = {
    logout,
    isAuthenticated,
    user,
  };

  // Configurer l'intercepteur API une seule fois au démarrage
  useEffect(() => {
    const cleanupInterceptor = setupApiInterceptor(authRef);
    return cleanupInterceptor; // Nettoyer à la destruction du composant
  }, []); // Pas de dépendances pour éviter la re-configuration

  return (
    <AuthContext.Provider value={value}>
      <MobileSplashGate isLoading={isLoading}>
        {!isLoading && children}
      </MobileSplashGate>

    </AuthContext.Provider>
  );
};

// 3. Créer un hook custom pour utiliser le contexte facilement
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
